import { createContext } from "react";
import {
  BookerByIdQuery,
  EnumDiscountUnit,
  Operator,
  Organization,
  Product,
} from "gql/generated";
import { i18nLanguage } from "i18n";
import { BookerPosition } from "widgets/booker/App";
import { CartSnapshot } from "../CartContextProvider";
import type { AddressSearchResponseValue } from "shared/components/organisms/SurveyQuestionInput/addressSearchResponse";
import type { MembershipCheckoutIntent } from "shared/utils/membershipIntent";

export type DefaultContact = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  address?: AddressSearchResponseValue | null;
};

export type UserOperator = {
  entityId: string;
  name: string;
  organizationIds?: string[];
};

export type PaymentRequestLineItem = {
  description: string;
  amount: number;
  quantity: number;
};

export type PaymentRequestAppliedDiscount = {
  discountId: string;
  name?: string;
  promoCode?: string;
  unit: EnumDiscountUnit;
  amount: number;
  discountAmount: number;
};

export type BookerContextValues = {
  booker: BookerByIdQuery["bookerById"] | null;
  product: Product | null;
  /** Every bundle's main product, in bundle order. `product` is always `products[0]`. */
  products: Product[];
  /** Multi-product merch store: browse a catalog, then one checkout for the whole cart. */
  isCatalog: boolean;
  /** How a catalog is browsed; `null` when the booker sells a single product. */
  catalogLayout: "list" | "grid" | null;
  owner: Operator | Organization | null;
  error: boolean;
  loading: boolean;
  onCloseBookerModal: () => void;
  isEmbedded: boolean;
  language: i18nLanguage;
  bookerPosition: BookerPosition;
  broadcastId?: string;
  broadcastResourceId?: string;
  defaultContact?: DefaultContact;
  userOperators?: UserOperator[];
  stripeTestMode?: boolean;
  /** Storefront has a live/sandbox link; widgets may render without a Stripe account. */
  storefrontIsPaired?: boolean;
  paymentRequestId?: string;
  paymentRequestVersion?: number;
  paymentRequestGrandTotal?: number;
  paymentRequestSubtotal?: number;
  paymentRequestTaxAmount?: number;
  paymentRequestTaxLines?: {
    name: string;
    ratePercent?: number | null;
    amount: number;
  }[];
  paymentRequestCustomerFee?: number | null;
  paymentRequestAdditionalLineItems?: PaymentRequestLineItem[];
  paymentRequestAppliedDiscount?: PaymentRequestAppliedDiscount;
  initialCartSnapshot?: CartSnapshot;
  readOnlySelection?: boolean;
  disableResetOnOpen?: boolean;
  invoiceAdminMode?: boolean;
  isStorefrontOwner?: boolean;
  /** Storefront scope for admin invoice recipient search (People). */
  storefrontId?: string;
  /** When the storefront app is opened under an org route, directory billing uses org listings. */
  organizationId?: string;
  /** Set by membership surfaces (renew / join / change tier); copy and tier preselection only. */
  membershipIntent?: MembershipCheckoutIntent;
};

export const BookerContext = createContext<BookerContextValues | null>(null);
